import React, { useEffect, useState, useRef } from 'react';
import { BookOpen, Globe, Award, ShieldCheck, CheckCircle2, Sparkles, Cpu, Layers, FileCheck, Check, Building2 } from 'lucide-react';
import { STATS_DATA, COMPANY_INFO } from '../data/companyData';

interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  label?: string;
}

const AnimatedNumber: React.FC<CounterProps> = ({ end, duration = 2000, suffix = '', prefix = '', label = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const val = Math.floor(progress * end);
      setCount(val);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
        setIsFinished(true);
      }
    };
    window.requestAnimationFrame(step);
  }, [hasStarted, end, duration]);

  const displayVal = `${prefix}${count.toLocaleString()}${suffix}`;
  const finalLabel = label ? `${label}: ${prefix}${end.toLocaleString()}${suffix}` : displayVal;

  return (
    <span
      ref={ref}
      role="status"
      aria-live={isFinished ? "polite" : "off"}
      aria-atomic="true"
      aria-label={isFinished ? finalLabel : displayVal}
    >
      {displayVal}
    </span>
  );
};

export const AboutSection: React.FC = () => {
  return (
    <section id="about" className="py-16 sm:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-purple-600" /> About ASAP LEARNING
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Pioneering the Future of Digital & Print Publishing
          </h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            ASAP LEARNING is a global publishing technology partner empowering educational institutions, academic presses, and commercial publishers through innovative AI-assisted prepress, typesetting, XML conversion, and PDF/UA accessibility.
          </p>
        </div>

        {/* Global Compliance & E-E-A-T Standards Banner */}
        <div className="mt-8 max-w-5xl mx-auto bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-6 flex flex-wrap items-center justify-around gap-4 text-center">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
            <span className="text-xs sm:text-sm font-bold text-slate-800">ISO 9001:2015 Quality Certified</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />
            <span className="text-xs sm:text-sm font-bold text-slate-800">ISO 14289-1 (PDF/UA) Standard</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-indigo-600 shrink-0" />
            <span className="text-xs sm:text-sm font-bold text-slate-800">W3C WCAG 2.1 Level AA</span>
          </div>
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-purple-600 shrink-0" />
            <span className="text-xs sm:text-sm font-bold text-slate-800">NISO JATS 1.3 / BITS 2.1 XML</span>
          </div>
        </div>

        {/* Feature Highlights Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              title: 'Publishing Excellence',
              desc: 'Decades of domain expertise handling complex mathematical typesetting, multi-language layouts, and K-12 textbook designs.',
              icon: BookOpen,
              color: 'text-blue-600 bg-blue-50 border-blue-100'
            },
            {
              title: 'Educational Content',
              desc: 'Tailored solutions for STEM, humanities, workbooks, teacher guides, and digital interactive web modules.',
              icon: Layers,
              color: 'text-purple-600 bg-purple-50 border-purple-100'
            },
            {
              title: 'Digital & Automation',
              desc: 'Proprietary AI workflows and scripts that accelerate formatting, XML schema matching, and quality validation.',
              icon: Cpu,
              color: 'text-emerald-600 bg-emerald-50 border-emerald-100'
            },
            {
              title: 'Global Delivery Hub',
              desc: 'Operating from Dehradun, India, serving premier publishing houses across the Americas, Europe, and Asia-Pacific.',
              icon: Globe,
              color: 'text-sky-600 bg-sky-50 border-sky-100'
            }
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-slate-50 border border-slate-200/80 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border mb-4 ${item.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Animated Counters Banner */}
        <div className="mt-16 bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 rounded-3xl p-8 sm:p-12 text-white shadow-2xl relative overflow-hidden">
          {/* Subtle Background Glow */}
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 divide-y sm:divide-y-0 sm:divide-x lg:divide-x divide-slate-800">
            {STATS_DATA.map((stat, i) => (
              <div key={i} className={`text-center space-y-2 ${i > 0 ? 'pt-6 sm:pt-0' : ''}`}>
                <p className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-emerald-300 tracking-tight">
                  <AnimatedNumber end={stat.value} suffix={stat.suffix} prefix={stat.prefix} label={stat.label} />
                </p>
                <p className="text-sm sm:text-base font-bold text-white tracking-wide">
                  {stat.label}
                </p>
                <p className="text-xs text-slate-400 max-w-xs mx-auto leading-normal">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* E-E-A-T Work Experience & Editorial Transparency Statement */}
        <div className="mt-12 text-center max-w-3xl mx-auto space-y-2 text-xs text-slate-500 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-200">
          <p className="font-semibold text-slate-700">
            Professional Experience & Editorial Standards Disclaimer:
          </p>
          <p>
            Project metrics and portfolio highlights reflect technical workflow expertise and prepress production executed by ASAP LEARNING subject matter specialists for educational and academic titles under publisher guidelines and standard production requirements.
          </p>
        </div>

      </div>
    </section>
  );
};
